import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Clock, Play, Square, CheckCircle2, Coffee, Download,
  Users, ScrollText, Settings, Pencil, BarChart3, AlertTriangle, TrendingUp, Target, BellRing,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { fmtMinHM } from "@/lib/utils";

const apiErr = (e, fb) => e?.response?.data?.detail || fb;
const today = () => new Date().toISOString().slice(0, 10);
const fmtMin = fmtMinHM;
const fmtDate = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" }); } catch { return s; } };

// Solicitud de corrección (empleado). Nunca sobrescribe el original: crea una petición auditada.
function CorreccionDialog({ workDayId, date, onClose }) {
  const [tipo, setTipo] = useState("start_time");
  const [valor, setValor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!motivo.trim()) { toast.error("El motivo es obligatorio"); return; }
    if (!valor.trim()) { toast.error("Indica el valor solicitado"); return; }
    const requested = tipo === "notes" ? { notes: valor } : { [tipo]: `${date}T${valor}:00` };
    setBusy(true);
    try {
      await api.post("/rh/corrections", { entity_type: "work_day", entity_id: workDayId, requested_value: requested, reason: motivo });
      toast.success("Corrección solicitada. Queda pendiente de aprobación.");
      onClose();
    } catch (e) { toast.error(apiErr(e, "No se pudo solicitar")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="rh-correccion-dialog">
        <DialogHeader><DialogTitle>Solicitar corrección — {fmtDate(date)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">No se cambia nada directamente: se envía una solicitud que un responsable aprobará o rechazará (queda histórico).</p>
          <div>
            <Label className="text-xs">¿Qué corregir?</Label>
            <Select value={tipo} onValueChange={(v) => { setTipo(v); setValor(""); }}>
              <SelectTrigger className="mt-1" data-testid="rh-corr-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="start_time">Hora de inicio</SelectItem>
                <SelectItem value="end_time">Hora de fin</SelectItem>
                <SelectItem value="notes">Observación / nota</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Valor solicitado</Label>
            {tipo === "notes"
              ? <Textarea value={valor} onChange={(e) => setValor(e.target.value)} className="mt-1" data-testid="rh-corr-valor" />
              : <Input type="time" value={valor} onChange={(e) => setValor(e.target.value)} className="mt-1 h-9" data-testid="rh-corr-valor" />}
          </div>
          <div><Label className="text-xs">Motivo (obligatorio)</Label><Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-1" placeholder="Ej.: olvidé fichar la salida" data-testid="rh-corr-motivo" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={busy} onClick={submit} data-testid="rh-corr-submit">Enviar solicitud</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function downloadCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiErr(e, "No se pudo exportar")); }
}

// ============================================================ JORNADA (fichaje)
function JornadaWidget({ cfg }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["rh-today"], queryFn: async () => (await api.get("/rh/jornada/today")).data, refetchInterval: 60000 });
  const [breakType, setBreakType] = useState(cfg.break_types?.[0] || "Pausa personal");
  const [busy, setBusy] = useState(false);
  const [corr, setCorr] = useState(false);
  const st = data?.state || "not_started";
  const act = async (fn) => { setBusy(true); try { await fn(); qc.invalidateQueries({ queryKey: ["rh-today"] }); qc.invalidateQueries({ queryKey: ["rh-today-qa"] }); } catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); } };

  const STATE_UI = {
    not_started: { label: "Sin iniciar", tone: "bg-slate-100 text-slate-600" },
    working: { label: "Jornada en curso", tone: "bg-emerald-100 text-emerald-700" },
    on_break: { label: "En pausa", tone: "bg-amber-100 text-amber-700" },
    ended: { label: "Jornada finalizada", tone: "bg-blue-100 text-blue-700" },
    confirmed: { label: "Confirmada ✓", tone: "bg-primary/10 text-primary" },
  }[st];

  return (
    <Card data-testid="rh-jornada-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base"><Clock className="h-5 w-5 text-primary" />Mi jornada de hoy</CardTitle>
        <Badge className={STATE_UI.tone} data-testid="rh-jornada-state">{STATE_UI.label}</Badge>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div><p className="text-xs text-muted-foreground">Fecha</p><p className="font-semibold capitalize">{fmtDate(data?.date || today())}</p></div>
          <div><p className="text-xs text-muted-foreground">Tiempo total</p><p className="font-semibold tabular-nums" data-testid="rh-total">{fmtMin(data?.duration_total_min)}</p></div>
          <div><p className="text-xs text-muted-foreground">Tiempo efectivo</p><p className="font-semibold tabular-nums" data-testid="rh-effective">{fmtMin(data?.duration_effective_min)}</p></div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {st === "not_started" && (
            <Button disabled={busy} onClick={() => act(() => api.post("/rh/jornada/start"))} data-testid="rh-start"><Play className="mr-1.5 h-4 w-4" />Iniciar jornada</Button>
          )}
          {st === "working" && (
            <>
              <div className="flex items-center gap-2">
                <Select value={breakType} onValueChange={setBreakType}>
                  <SelectTrigger className="h-9 w-40" data-testid="rh-break-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg.break_types || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" disabled={busy} onClick={() => act(() => api.post("/rh/jornada/break/start", { break_type: breakType }))} data-testid="rh-break-start"><Coffee className="mr-1.5 h-4 w-4" />Iniciar pausa</Button>
              </div>
              <Button variant="destructive" disabled={busy} onClick={() => act(() => api.post("/rh/jornada/end"))} data-testid="rh-end"><Square className="mr-1.5 h-4 w-4" />Finalizar jornada</Button>
            </>
          )}
          {st === "on_break" && (
            <Button disabled={busy} onClick={() => act(() => api.post("/rh/jornada/break/end"))} data-testid="rh-break-end"><Play className="mr-1.5 h-4 w-4" />Finalizar pausa</Button>
          )}
          {st === "ended" && (
            <>
              <Button disabled={busy} onClick={() => act(() => api.post("/rh/jornada/confirm"))} data-testid="rh-confirm"><CheckCircle2 className="mr-1.5 h-4 w-4" />Confirmar jornada</Button>
              <Button variant="outline" disabled={busy} onClick={() => act(() => api.post("/rh/jornada/reopen"))} data-testid="rh-reopen"><Play className="mr-1.5 h-4 w-4" />Reabrir jornada</Button>
            </>
          )}
          {st === "confirmed" && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground">Has confirmado tu jornada de hoy. ¡Buen trabajo!</p>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post("/rh/jornada/reopen"))} data-testid="rh-reopen"><Play className="mr-1.5 h-4 w-4" />Reabrir (la cerré por error)</Button>
            </div>
          )}
          {data?.work_day && (
            <Button size="sm" variant="ghost" onClick={() => setCorr(true)} data-testid="rh-request-correction"><Pencil className="mr-1.5 h-4 w-4" />Solicitar corrección</Button>
          )}
        </div>

        {(data?.entries || []).length > 0 && (
          <div className="rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Inicio</th><th className="p-2 text-left">Fin</th><th className="p-2 text-right">Min</th></tr></thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="p-2">{e.type === "work" ? "Trabajo" : `Pausa${e.notes ? ` · ${e.notes}` : ""}`}</td>
                    <td className="p-2 tabular-nums">{e.start_time ? new Date(e.start_time).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="p-2 tabular-nums">{e.end_time ? new Date(e.end_time).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{e.duration_minutes || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {corr && data?.work_day && <CorreccionDialog workDayId={data.work_day.id} date={data.date} onClose={() => setCorr(false)} />}
    </Card>
  );
}

function MisJornadas() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [corr, setCorr] = useState(null);
  const { data } = useQuery({ queryKey: ["rh-mine-jornada", month], queryFn: async () => (await api.get(`/rh/jornada?month=${month}`)).data });
  const items = data?.items || [];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Mis jornadas</CardTitle>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="rh-mine-month" />
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-sm text-muted-foreground">Sin jornadas registradas este mes.</p> : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="p-2 text-left">Fecha</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Efectivo</th><th className="p-2 text-left">Estado</th><th className="p-2 text-right">Corregir</th></tr></thead>
              <tbody>{items.map((w) => (
                <tr key={w.id} className="border-t">
                  <td className="p-2 capitalize">{fmtDate(w.date)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtMin(w.duration_total_min)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtMin(w.duration_effective_min)}</td>
                  <td className="p-2">{w.status}</td>
                  <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => setCorr(w)} data-testid={`rh-corr-row-${w.date}`}><Pencil className="h-3.5 w-3.5" /></Button></td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </CardContent>
      {corr && <CorreccionDialog workDayId={corr.id} date={corr.date} onClose={() => setCorr(null)} />}
    </Card>
  );
}

// ============================================================ MANAGER
function PendingBanner() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["rh-pending-today"], queryFn: async () => (await api.get("/rh/manager/pending-today")).data, refetchInterval: 120000 });
  const count = data?.count || 0;
  if (count === 0) return null;
  const remind = async () => {
    setBusy(true);
    try { const r = await api.post("/rh/manager/remind-now"); toast.success(`Recordatorio enviado a ${r.data.sent} ${r.data.sent === 1 ? "persona" : "personas"}`); qc.invalidateQueries({ queryKey: ["rh-pending-today"] }); }
    catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); }
  };
  const names = (data.items || []).map((i) => i.employee_name).join(", ");
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between" data-testid="rh-pending-banner">
      <div className="flex items-start gap-2">
        <BellRing className="mt-0.5 h-4 w-4 shrink-0" />
        <div><strong>{count} {count === 1 ? "persona tiene" : "personas tienen"} la jornada de hoy sin confirmar.</strong> <span className="text-amber-800">{names}</span></div>
      </div>
      <Button size="sm" variant="outline" className="shrink-0 border-amber-400" disabled={busy} onClick={remind} data-testid="rh-remind-now"><BellRing className="mr-1.5 h-4 w-4" />Enviar recordatorio</Button>
    </div>
  );
}

function EquipoJornadas() {
  const [from, setFrom] = useState(today().slice(0, 8) + "01");
  const [to, setTo] = useState(today());
  const { data } = useQuery({ queryKey: ["rh-jornada-all", from, to], queryFn: async () => (await api.get(`/rh/jornada/all?date_from=${from}&date_to=${to}`)).data });
  const items = data?.items || [];
  return (
    <div className="space-y-4">
      <PendingBanner />
      <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base">Jornadas del equipo</CardTitle>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="rh-eq-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="rh-eq-to" />
          <Button variant="outline" size="sm" onClick={() => downloadCsv(`/rh/reports/jornada.csv?date_from=${from}&date_to=${to}`, "jornada.csv")} data-testid="rh-eq-export"><Download className="mr-1 h-4 w-4" />CSV</Button>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-sm text-muted-foreground">Sin datos en el rango.</p> : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="p-2 text-left">Empleado</th><th className="p-2 text-left">Fecha</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Efectivo</th><th className="p-2 text-left">Estado</th></tr></thead>
              <tbody>{items.map((w) => (<tr key={w.id} className="border-t"><td className="p-2">{w.employee_name}</td><td className="p-2 capitalize">{fmtDate(w.date)}</td><td className="p-2 text-right tabular-nums">{fmtMin(w.duration_total_min)}</td><td className="p-2 text-right tabular-nums">{fmtMin(w.duration_effective_min)}</td><td className="p-2">{w.status}</td></tr>))}</tbody>
            </table>
          </div>
        )}
      </CardContent>
      </Card>
    </div>
  );
}

function CorreccionesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["rh-corr"], queryFn: async () => (await api.get("/rh/corrections?status=pending")).data });
  const items = data?.items || [];
  const review = async (c, decision) => {
    const comment = decision === "reject" ? (window.prompt("Motivo del rechazo:") || "") : (window.prompt("Comentario (opcional):") || "");
    try { await api.post(`/rh/corrections/${c.id}/review`, { decision, comment }); toast.success(decision === "approve" ? "Aprobada" : "Rechazada"); qc.invalidateQueries({ queryKey: ["rh-corr"] }); }
    catch (e) { toast.error(apiErr(e, "Error")); }
  };
  return (
    <Card><CardHeader><CardTitle className="text-base">Correcciones pendientes</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? <p className="text-sm text-muted-foreground">No hay correcciones pendientes.</p> : items.map((c) => (
          <div key={c.id} className="rounded-lg border p-3">
            <p className="text-sm font-semibold">{c.employee_name} · {c.entity_type}</p>
            <p className="text-xs text-muted-foreground">Motivo: {c.reason}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => review(c, "approve")} data-testid={`rh-corr-approve-${c.id}`}>Aprobar</Button>
              <Button size="sm" variant="outline" onClick={() => review(c, "reject")} data-testid={`rh-corr-reject-${c.id}`}>Rechazar</Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AuditoriaPanel() {
  const { data } = useQuery({ queryKey: ["rh-audit"], queryFn: async () => (await api.get("/rh/audit?limit=200")).data });
  const items = data?.items || [];
  return (
    <Card><CardHeader><CardTitle className="text-base">Auditoría</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-[60vh] overflow-auto rounded-lg border">
          <table className="w-full text-xs"><thead className="sticky top-0 bg-muted/60"><tr><th className="p-2 text-left">Fecha</th><th className="p-2 text-left">Usuario</th><th className="p-2 text-left">Acción</th><th className="p-2 text-left">Entidad</th></tr></thead>
            <tbody>{items.map((a) => (<tr key={a.id} className="border-t"><td className="p-2 tabular-nums">{new Date(a.timestamp).toLocaleString("es-ES")}</td><td className="p-2">{a.user_email}</td><td className="p-2">{a.action}</td><td className="p-2">{a.entity_type}</td></tr>))}</tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================ SUPERVISIÓN (Dirección)
const fmtHm = fmtMinHM;

function TrendDialog({ employee, year, onClose }) {
  const { data } = useQuery({
    queryKey: ["rh-trend", employee.employee_id, year],
    queryFn: async () => (await api.get(`/rh/manager/employee-trend?employee_id=${employee.employee_id}&granularity=month&year=${year}`)).data,
  });
  const series = data?.series || [];
  const max = Math.max(1, ...series.map((s) => s.effective_min));
  const MESES = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="rh-trend-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" />Evolución de {employee.employee_name} · {year}</DialogTitle></DialogHeader>
        {series.length === 0 ? <p className="text-sm text-muted-foreground">Sin jornadas registradas este año.</p> : (
          <div className="flex items-end gap-2 overflow-x-auto pt-4" style={{ height: 220 }}>
            {series.map((s) => {
              const h = Math.round((s.effective_min / max) * 170);
              const mi = parseInt(s.bucket, 10);
              return (
                <div key={s.bucket} className="flex min-w-[42px] flex-1 flex-col items-center justify-end gap-1" data-testid={`rh-trend-bar-${s.bucket}`}>
                  <span className="text-[10px] tabular-nums text-muted-foreground">{fmtHm(s.effective_min)}</span>
                  <div className="w-full rounded-t bg-primary/80" style={{ height: `${h}px` }} title={`${s.days} días`} />
                  <span className="text-[11px] text-muted-foreground">{MESES[mi] || s.bucket}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">Barras = horas efectivas por mes. Pasa el ratón para ver los días trabajados.</p>
      </DialogContent>
    </Dialog>
  );
}

const ALERT_UI = {
  ok: { label: "Correcto", cls: "bg-emerald-100 text-emerald-700" },
  over: { label: "Exceso de horas", cls: "bg-rose-100 text-rose-700" },
  under: { label: "Por debajo", cls: "bg-amber-100 text-amber-700" },
};

function SupervisionPanel() {
  const [period, setPeriod] = useState("week");
  const [anchorDate, setAnchorDate] = useState(today());
  const [anchorMonth, setAnchorMonth] = useState(today().slice(0, 7));
  const [anchorYear, setAnchorYear] = useState(Number(today().slice(0, 4)));
  const [trend, setTrend] = useState(null);
  const anchor = period === "week" ? anchorDate : period === "month" ? `${anchorMonth}-01` : `${anchorYear}-01-01`;
  const { data } = useQuery({
    queryKey: ["rh-hours", period, anchor],
    queryFn: async () => (await api.get(`/rh/manager/hours?period=${period}&anchor=${anchor}`)).data,
  });
  const rows = data?.rows || [];
  const alertsCount = data?.alerts_count || 0;

  React.useEffect(() => {
    if (alertsCount > 0 && data) {
      const names = (data.alerts || []).map((a) => a.employee_name).join(", ");
      toast.warning(`⚠️ ${alertsCount} ${alertsCount === 1 ? "empleado" : "empleados"} fuera de rango de horas`, { description: names, duration: 6000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.start, data?.end, alertsCount]);

  return (
    <div className="space-y-4">
      <Card data-testid="rh-supervision-panel">
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-5 w-5 text-primary" />Supervisión de horas</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border p-0.5" data-testid="rh-sup-period">
              {[["week", "Semana"], ["month", "Mes"], ["year", "Año"]].map(([v, l]) => (
                <button key={v} onClick={() => setPeriod(v)} data-testid={`rh-sup-period-${v}`}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${period === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>{l}</button>
              ))}
            </div>
            {period === "week" && <Input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} className="h-9 w-40" data-testid="rh-sup-week" />}
            {period === "month" && <Input type="month" value={anchorMonth} onChange={(e) => setAnchorMonth(e.target.value)} className="h-9 w-40" data-testid="rh-sup-month" />}
            {period === "year" && <Input type="number" value={anchorYear} onChange={(e) => setAnchorYear(Number(e.target.value))} className="h-9 w-28" data-testid="rh-sup-year" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {data && (
            <p className="text-xs text-muted-foreground">
              Periodo <strong>{data.start}</strong> → <strong>{data.end}</strong>. Objetivo diario: <strong>{fmtHm(data.target_daily_min)}</strong> (jornada semanal {data.weekly_hours}h ÷ 5). Se marca <strong>Exceso</strong> por encima de +{data.over_pct}% y <strong>Por debajo</strong> por debajo de −{data.under_pct}% respecto a la media efectiva por día trabajado.
            </p>
          )}
          {alertsCount > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="rh-sup-alert-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div><strong>{alertsCount} {alertsCount === 1 ? "empleado" : "empleados"} fuera de rango.</strong> Revisa quién está haciendo muchas horas de más o de menos.</div>
            </div>
          )}
          {rows.length === 0 ? <p className="text-sm text-muted-foreground">Sin jornadas registradas en el periodo.</p> : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Empleado</th>
                    <th className="p-2 text-right">Días</th>
                    <th className="p-2 text-right">Horas efectivas</th>
                    <th className="p-2 text-right">Media/día</th>
                    <th className="p-2 text-right">Objetivo/día</th>
                    <th className="p-2 text-right">Desviación</th>
                    <th className="p-2 text-center">Estado</th>
                    <th className="p-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const ui = ALERT_UI[r.alert] || ALERT_UI.ok;
                    return (
                      <tr key={r.employee_id} className={`border-t ${r.alert !== "ok" ? "bg-rose-50/40" : ""}`} data-testid={`rh-sup-row-${r.employee_id}`}>
                        <td className="p-2 font-medium">{r.employee_name}</td>
                        <td className="p-2 text-right tabular-nums">{r.days}</td>
                        <td className="p-2 text-right tabular-nums">{fmtHm(r.effective_min)}</td>
                        <td className="p-2 text-right tabular-nums">{fmtHm(r.avg_daily_min)}</td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtHm(r.target_daily_min)}</td>
                        <td className={`p-2 text-right tabular-nums font-semibold ${r.deviation_pct > 0 ? "text-rose-600" : r.deviation_pct < 0 ? "text-amber-600" : "text-muted-foreground"}`}>{r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%</td>
                        <td className="p-2 text-center"><Badge className={ui.cls} data-testid={`rh-sup-badge-${r.employee_id}`}>{ui.label}</Badge></td>
                        <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => setTrend(r)} data-testid={`rh-sup-trend-${r.employee_id}`}><TrendingUp className="h-3.5 w-3.5" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {trend && <TrendDialog employee={trend} year={period === "year" ? anchorYear : Number((anchor || today()).slice(0, 4))} onClose={() => setTrend(null)} />}
    </div>
  );
}

// ============================================================ OBJETIVOS (jornada por persona)
function ObjetivosPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["rh-targets"], queryFn: async () => (await api.get("/rh/targets")).data });
  const [edits, setEdits] = useState({});
  const globalWeekly = data?.global_weekly ?? 40;
  const items = data?.items || [];
  const val = (it) => (edits[it.employee_id] !== undefined ? edits[it.employee_id] : (it.weekly_hours ?? ""));
  const save = async (eid, raw) => {
    try {
      const r = await api.put(`/rh/targets/${eid}`, { weekly_hours: raw === "" ? null : Number(raw) });
      toast.success("Objetivo guardado");
      setEdits((e) => ({ ...e, [eid]: r.data.weekly_hours ?? "" }));
      qc.invalidateQueries({ queryKey: ["rh-targets"] });
      qc.invalidateQueries({ queryKey: ["rh-hours"] });
    } catch (e) { toast.error(apiErr(e, "Error")); }
  };
  return (
    <Card data-testid="rh-objetivos-panel">
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Target className="h-5 w-5 text-primary" />Objetivo de jornada por persona</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">Define las <strong>horas semanales</strong> de cada trabajador (p. ej. 20h media jornada, 35h, 40h completa). Se usa para calcular el objetivo diario y las alertas de supervisión. Déjalo vacío para usar la jornada general de la empresa (<strong>{globalWeekly}h</strong>).</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="p-2 text-left">Trabajador</th><th className="p-2 text-left">Rol</th><th className="p-2 text-right">Horas/semana</th><th className="p-2 text-right">Objetivo/día</th><th className="p-2 text-right">Acción</th></tr></thead>
            <tbody>
              {items.map((it) => {
                const eff = (val(it) === "" ? globalWeekly : Number(val(it)));
                return (
                  <tr key={it.employee_id} className="border-t" data-testid={`rh-target-row-${it.employee_id}`}>
                    <td className="p-2 font-medium">{it.name}</td>
                    <td className="p-2 text-muted-foreground">{it.role}</td>
                    <td className="p-2 text-right">
                      <Input type="number" min="0" max="80" className="ml-auto h-8 w-24 text-right tabular-nums"
                        value={val(it)} placeholder={`${globalWeekly} (general)`}
                        onChange={(e) => setEdits((s) => ({ ...s, [it.employee_id]: e.target.value }))}
                        data-testid={`rh-target-input-${it.employee_id}`} />
                    </td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtHm(Math.round(eff / 5 * 60))}</td>
                    <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => save(it.employee_id, val(it))} data-testid={`rh-target-save-${it.employee_id}`}>Guardar</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================ CONFIG (admin)
function ConfigPanel({ cfg, refetch }) {
  const [local, setLocal] = useState(cfg);
  const [busy, setBusy] = useState(false);
  const toggle = async (enabled) => {
    setBusy(true);
    try { await api.post("/rh/config/toggle", { enabled }); toast.success(enabled ? "Módulo ACTIVADO" : "Módulo desactivado"); refetch(); }
    catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/rh/config", {
        timezone: local.timezone, weekly_hours: Number(local.weekly_hours),
        inactivity_threshold_min: Number(local.inactivity_threshold_min),
        alert_over_pct: Number(local.alert_over_pct),
        alert_under_pct: Number(local.alert_under_pct),
        reminder_enabled: !!local.reminder_enabled,
        reminder_hour: Number(local.reminder_hour),
      });
      toast.success("Configuración guardada"); refetch();
    } catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="rh-config-panel">
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Settings className="h-5 w-5" />Configuración del módulo</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className={`flex items-center justify-between rounded-lg border p-4 ${cfg.enabled ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
          <div>
            <p className="font-semibold">{cfg.enabled ? "Módulo ACTIVO" : "Módulo desactivado (oculto para el equipo)"}</p>
            <p className="text-xs text-muted-foreground">Actívalo cuando la empresa vaya a empezar a usar el registro horario. Mientras esté apagado, solo lo ves tú (administración).</p>
          </div>
          <Switch checked={!!cfg.enabled} disabled={busy} onCheckedChange={toggle} data-testid="rh-config-toggle" />
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div><Label className="text-xs">Zona horaria</Label><Input value={local.timezone} onChange={(e) => setLocal({ ...local, timezone: e.target.value })} className="mt-1 h-9" /></div>
          <div><Label className="text-xs">Jornada semanal (h)</Label><Input type="number" value={local.weekly_hours} onChange={(e) => setLocal({ ...local, weekly_hours: e.target.value })} className="mt-1 h-9" /></div>
          <div><Label className="text-xs">Umbral inactividad (min)</Label><Input type="number" value={local.inactivity_threshold_min} onChange={(e) => setLocal({ ...local, inactivity_threshold_min: e.target.value })} className="mt-1 h-9" /></div>
        </div>
        <div className="rounded-lg border bg-muted/20 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><BarChart3 className="h-4 w-4 text-primary" />Alertas de supervisión</p>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Aviso por exceso (% sobre objetivo)</Label><Input type="number" value={local.alert_over_pct ?? 15} onChange={(e) => setLocal({ ...local, alert_over_pct: e.target.value })} className="mt-1 h-9" data-testid="rh-config-over" /></div>
            <div><Label className="text-xs">Aviso por defecto (% bajo objetivo)</Label><Input type="number" value={local.alert_under_pct ?? 15} onChange={(e) => setLocal({ ...local, alert_under_pct: e.target.value })} className="mt-1 h-9" data-testid="rh-config-under" /></div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">En la pestaña <strong>Supervisión</strong>, se marca en rojo/ámbar a quien se desvíe de la media efectiva por día trabajado más de estos porcentajes.</p>
        </div>
        <div className="rounded-lg border bg-muted/20 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><BellRing className="h-4 w-4 text-primary" />Recordatorio de fichaje</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Avisar a quien deje la jornada sin confirmar</p>
              <p className="text-xs text-muted-foreground">Envía un aviso (in-app + email) a partir de la hora indicada a quien no haya confirmado su jornada del día.</p>
            </div>
            <Switch checked={!!local.reminder_enabled} onCheckedChange={(v) => setLocal({ ...local, reminder_enabled: v })} data-testid="rh-config-reminder-toggle" />
          </div>
          <div className="mt-3 w-40">
            <Label className="text-xs">Hora del recordatorio</Label>
            <Input type="number" min="0" max="23" value={local.reminder_hour ?? 18} onChange={(e) => setLocal({ ...local, reminder_hour: e.target.value })} className="mt-1 h-9" data-testid="rh-config-reminder-hour" />
          </div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="rh-config-save">Guardar configuración</Button>
        <p className="text-[11px] text-muted-foreground">Este módulo registra la jornada ordinaria. No gestiona pagos, pluses, descansos compensatorios ni nóminas.</p>
      </CardContent>
    </Card>
  );
}

// ============================================================ PAGE
export default function RegistroHorarioPage() {
  const { data: cfg, refetch } = useQuery({ queryKey: ["rh-config"], queryFn: async () => (await api.get("/rh/config")).data });
  if (!cfg) return <div className="p-8 text-sm text-muted-foreground">Cargando…</div>;
  const isManager = cfg.is_manager, isAdmin = cfg.is_admin;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="registro-horario-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">Registro Horario</h1>
        <p className="text-sm text-muted-foreground">Jornada ordinaria del equipo. Sin vigilancia: solo registro de entrada, pausas y salida.</p>
      </div>
      {!cfg.enabled && isAdmin && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="rh-disabled-banner">
          El módulo está <strong>desactivado</strong>. El equipo no lo ve todavía. Actívalo desde la pestaña <strong>Configuración</strong> cuando queráis empezar a usarlo.
        </div>
      )}
      {!cfg.enabled && !isAdmin && (
        <div className="rounded-lg border border-border bg-muted/30 p-6 text-sm text-muted-foreground" data-testid="rh-not-active">
          El registro horario todavía no está activo. Cuando Administración lo active, podrás fichar tu jornada desde aquí y desde el atajo de la cabecera.
        </div>
      )}
      {(cfg.enabled || isAdmin) && (
        <Tabs defaultValue={cfg.enabled ? "jornada" : "config"}>
          <TabsList className="flex-wrap h-auto">
            {cfg.enabled && <TabsTrigger value="jornada" data-testid="rh-tab-jornada"><Clock className="mr-1.5 h-4 w-4" />Mi jornada</TabsTrigger>}
            {cfg.enabled && isManager && <TabsTrigger value="equipo" data-testid="rh-tab-equipo"><Users className="mr-1.5 h-4 w-4" />Equipo</TabsTrigger>}
            {cfg.enabled && isManager && <TabsTrigger value="supervision" data-testid="rh-tab-supervision"><BarChart3 className="mr-1.5 h-4 w-4" />Supervisión</TabsTrigger>}
            {cfg.enabled && isManager && <TabsTrigger value="objetivos" data-testid="rh-tab-objetivos"><Target className="mr-1.5 h-4 w-4" />Objetivos</TabsTrigger>}
            {cfg.enabled && isManager && <TabsTrigger value="correcciones" data-testid="rh-tab-corr"><Pencil className="mr-1.5 h-4 w-4" />Correcciones</TabsTrigger>}
            {cfg.enabled && isManager && <TabsTrigger value="auditoria" data-testid="rh-tab-audit"><ScrollText className="mr-1.5 h-4 w-4" />Auditoría</TabsTrigger>}
            {isAdmin && <TabsTrigger value="config" data-testid="rh-tab-config"><Settings className="mr-1.5 h-4 w-4" />Configuración</TabsTrigger>}
          </TabsList>

          {cfg.enabled && <TabsContent value="jornada" className="mt-4 space-y-4"><JornadaWidget cfg={cfg} /><MisJornadas /></TabsContent>}
          {cfg.enabled && isManager && <TabsContent value="equipo" className="mt-4"><EquipoJornadas /></TabsContent>}
          {cfg.enabled && isManager && <TabsContent value="supervision" className="mt-4"><SupervisionPanel /></TabsContent>}
          {cfg.enabled && isManager && <TabsContent value="objetivos" className="mt-4"><ObjetivosPanel /></TabsContent>}
          {cfg.enabled && isManager && <TabsContent value="correcciones" className="mt-4"><CorreccionesPanel /></TabsContent>}
          {cfg.enabled && isManager && <TabsContent value="auditoria" className="mt-4"><AuditoriaPanel /></TabsContent>}
          {isAdmin && <TabsContent value="config" className="mt-4"><ConfigPanel cfg={cfg} refetch={refetch} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
}
